import { toast } from 'react-toastify';
import { ApiError } from '../types';

export class AppError extends Error {
  public statusCode: number;
  public isOperational: boolean;

  constructor(message: string, statusCode: number = 500, isOperational: boolean = true) {
    super(message);
    this.statusCode = statusCode;
    this.isOperational = isOperational;
    
    Error.captureStackTrace(this, this.constructor);
  }
}

export const handleApiError = (error: ApiError | Error, context?: string): void => {
  console.error('API Error:', error);

  let message = 'An unexpected error occurred';
  let type: 'error' | 'warning' = 'error';

  if ('error' in error && error.error) {
    message = error.error;
    
    // Determine toast type based on status code
    if (error.status) {
      if (error.status >= 400 && error.status < 500) {
        type = 'warning';
      } else if (error.status >= 500) {
        type = 'error';
      }
    }

    // Add context if provided
    if (context) {
      message = `${context}: ${message}`;
    }

    // Show detailed errors in development
    if (process.env.NODE_ENV === 'development' && error.details) {
      console.error('Error details:', error.details);
      if (Array.isArray(error.details)) {
        message += ` (${error.details.join(', ')})`;
      } else {
        message += ` (${error.details})`;
      }
    }
  } else if (error instanceof Error) {
    message = error.message;
    if (context) {
      message = `${context}: ${message}`;
    }
  }

  // Show toast notification
  switch (type) {
    case 'error':
      toast.error(message);
      break;
    case 'warning':
      toast.warn(message);
      break;

  }
};

export const handleSuccess = (message: string): void => {
  toast.success(message);
};

export const handleInfo = (message: string): void => {
  toast.info(message);
};

export const handleWarning = (message: string): void => {
  toast.warn(message);
};

// Validation helpers
export const validateRequired = (value: any, fieldName: string): string | null => {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return `${fieldName} is required`;
  }
  return null;
};

export const validateEmail = (email: string): string | null => {
  if (!email) return null;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }
  return null;
};

export const validatePhone = (phone: string): string | null => {
  if (!phone) return null;
  
  const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
  if (!phoneRegex.test(phone.replace(/[\s\-()]/g, ''))) {
    return 'Please enter a valid phone number';
  }
  return null;
};

export const validateTimeRange = (startTime: string, endTime: string): string | null => {
  if (!startTime || !endTime) return null;
  
  const start = new Date(`2000-01-01 ${startTime}`);
  const end = new Date(`2000-01-01 ${endTime}`);
  
  if (start >= end) {
    return 'End time must be after start time';
  }
  return null;
};

export const validateDateRange = (startDate: Date, endDate: Date): string | null => {
  if (startDate >= endDate) {
    return 'End date must be after start date';
  }
  return null;
};

// Form validation helper
export const validateForm = (data: Record<string, any>, rules: Record<string, (value: any) => string | null>): Record<string, string> => {
  const errors: Record<string, string> = {};
  
  Object.keys(rules).forEach(field => {
    const error = rules[field](data[field]);
    if (error) {
      errors[field] = error;
    }
  });
  
  return errors;
};

// Network error detection
export const isNetworkError = (error: any): boolean => {
  return !error.response && error.request;
};

// Retry helper for failed requests
export const withRetry = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        break;
      }
      
      // Only retry on network errors or 5xx server errors
      if (isNetworkError(error) || ((error as any).status && (error as any).status >= 500)) {
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
        continue;
      } else {
        break; // Don't retry client errors
      }
    }
  }
  
  throw lastError;
};

// Offline detection
export const isOnline = (): boolean => {
  return navigator.onLine;
};

export const handleOfflineError = (): void => {
  if (!isOnline()) {
    toast.error('You are currently offline. Please check your internet connection.');
  }
};

// Generic error boundary handler
export const logError = (error: Error, errorInfo?: any): void => {
  console.error('Application Error:', error);
  if (errorInfo) {
    console.error('Error Info:', errorInfo);
  }
  
  // In production, you might want to send this to an error reporting service
  if (process.env.NODE_ENV === 'production') {
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }
};